/**
 * "Copy to project": pictures picked on a skill page, waiting for a board.
 * -------------------------------------------------------------
 * Pressed beside Send to Miro on the Amazon, Audit, Visual Language and
 * Design Works pages. Nothing is copied at that moment -- which project they
 * are for is not known yet. What is written down is what was picked; every
 * project page, open or opened later, then offers to bring them in.
 *
 * ONE COPY, GLOBAL (Huanrui, 2026-09-30)
 *
 * There is at most one waiting at a time, and a newer one replaces it.
 * Confirmed on one board, it is imported there and gone everywhere; cancelled
 * on one, it is gone everywhere. Left alone, it waits for as long as it takes.
 * Putting the same pictures on a second board is done on the canvas, by
 * copying them there and pasting on the other.
 *
 * Browser storage rather than the server: it is one person, one browser, a
 * few minutes, and every tab of this site reads the same storage and is told
 * when it changes -- which is what makes the offer vanish from the other open
 * boards the moment one of them answers it.
 *
 * Not on the system clipboard, and deliberately not reachable with Ctrl+V:
 * the board's Ctrl+V pastes whatever was copied last, and this is answered by
 * the offer alone.
 */

import type { Market } from './amazonService'
import type { CanvasEdge, CanvasNode } from './canvasService'

/** One picture waiting, as the page that picked it knew it. */
export type ClipItem =
  | {
      kind: 'library'
      /** The asset. Copied into the project when the offer is confirmed. */
      id: string
      thumb: string
      title: string
      width?: number
      height?: number
    }
  | {
      /**
       * A picture in one of this person's projects -- the generate and
       * upscale pages' own -- shown in the offer. What is copied is `board`.
       */
      kind: 'file'
      id: string
      thumb: string
      title: string
    }
  | {
      kind: 'amazon'
      market: Market
      /** The picture's address on Amazon, as the page showed it. */
      url: string
      asin: string
      title: string
      thumb: string
    }
  | {
      /** A Google Images pick (2026-10-14): fetched from its site when taken. */
      kind: 'web'
      url: string
      thumb: string
      title: string
      page: string
      site: string
      /** Unsplash, from the External Library: where from, and the credit kept. */
      source?: 'google' | 'unsplash'
      credit?: string
    }

export interface ProjectClip {
  /** Tells one copy from the next, so an answer only ever settles its own. */
  token: string
  /** Where they were picked, for the offer and the frame's name. */
  from: string
  at: number
  items: ClipItem[]
  /**
   * Copy to Project from the generate and upscale pages (Huanrui,
   * 2026-10-07): the card that made the pictures and the pictures, wired, as
   * a piece of a board. File ids are the ones in the person's own project;
   * `files` are copied into the board's project when the offer is taken, and
   * the ids swapped for the copies'.
   */
  board?: { nodes: CanvasNode[]; edges: CanvasEdge[]; files: string[] }
}

const KEY = 'ww.project-clip'

/*
 * No ceiling on how many (Huanrui, 2026-09-30). A board draws smaller copies
 * of large pictures, and the import runs a few at a time however many there
 * are.
 */

export function putClip(from: string, items: ClipItem[], board?: ProjectClip['board']): ProjectClip {
  const clip: ProjectClip = {
    token: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    from,
    at: Date.now(),
    items,
    ...(board ? { board } : {}),
  }
  window.localStorage.setItem(KEY, JSON.stringify(clip))
  return clip
}

export function readClip(): ProjectClip | null {
  try {
    const clip = JSON.parse(window.localStorage.getItem(KEY) ?? 'null') as ProjectClip | null
    return clip && typeof clip.token === 'string' && Array.isArray(clip.items) && clip.items.length > 0
      ? clip
      : null
  } catch (error) {
    console.warn('the waiting copy could not be read', error)
    return null
  }
}

/**
 * Settle one copy: take it away, and say whether it was still there.
 *
 * Only if it is still the same copy. Two open boards answering at once, or a
 * newer copy made in the meantime, must not have one answer settle the other.
 */
export function takeClip(token: string): ProjectClip | null {
  const clip = readClip()
  if (!clip || clip.token !== token) return null
  try {
    window.localStorage.removeItem(KEY)
  } catch (error) {
    console.warn('the waiting copy could not be cleared', error)
  }
  return clip
}

/** Told whenever a copy is made or settled, in this tab or another one. */
export function watchClip(onChange: (clip: ProjectClip | null) => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === KEY) onChange(readClip())
  }
  const onShow = () => {
    if (document.visibilityState === 'visible') onChange(readClip())
  }
  window.addEventListener('storage', onStorage)
  document.addEventListener('visibilitychange', onShow)
  return () => {
    window.removeEventListener('storage', onStorage)
    document.removeEventListener('visibilitychange', onShow)
  }
}
